export function useBlogSeo({
  title,
  description,
  keywords,
  slug,
  image,
  postData,
  faqItems,
  howToSteps,
  lang = "en",
}) {
  const config = useRuntimeConfig();
  const cdnUrl = config.public.cdnUrl;
  const baseUrl = config.public.baseUrl + "/blog";

  const pageUrl = `${baseUrl}/${lang}/${slug}`;
  const imageUrl = `${cdnUrl}${image}`;
  const logoUrl = `${cdnUrl}/static/images/travvir-logo.png`;

  const ogLocaleMap = { en: "en_US", fr: "fr_FR", es: "es_ES", de: "de_DE" };
  const ogLocale = ogLocaleMap[lang] ?? "en_US";

  useSeoMeta({
    title,
    description,
    keywords,
    ogTitle: title,
    ogDescription: description,
    ogType: "article",
    ogUrl: pageUrl,
    ogImage: imageUrl,
    ogLocale,
    twitterCard: "summary_large_image",
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: imageUrl,
    articlePublishedTime: postData.date,
    articleModifiedTime: postData.updatedAt ?? postData.date,
    articleAuthor: "Travvir",
    articleSection: postData.categories?.[0] ?? "Virtual Tours",
    articleTag: postData.categories ?? [],
  });

  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: postData.title,
      description: postData.excerpt,
      image: {
        "@type": "ImageObject",
        url: imageUrl,
        width: 1200,
        height: 630,
      },
      datePublished: postData.date,
      dateModified: postData.updatedAt ?? postData.date,
      author: {
        "@type": "Organization",
        name: "Travvir",
        url: "https://travvir.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Travvir",
        logo: { "@type": "ImageObject", url: logoUrl },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": pageUrl },
      url: pageUrl,
      keywords: keywords,
      articleSection: postData.categories?.[0] ?? "Virtual Tours",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://travvir.com",
        },
        { "@type": "ListItem", position: 2, name: "Blog", item: `${baseUrl}/` },
        {
          "@type": "ListItem",
          position: 3,
          name: postData.title,
          item: pageUrl,
        },
      ],
    },
  ];

  if (Array.isArray(faqItems) && faqItems.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
  }

  if (Array.isArray(howToSteps) && howToSteps.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: postData.title,
      description: postData.excerpt,
      image: { "@type": "ImageObject", url: imageUrl },
      step: howToSteps.map((step, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: step.name,
        text: step.text,
        ...(step.url ? { url: step.url } : {}),
      })),
    });
  }

  useHead({
    script: schemas.map((schema) => ({
      type: "application/ld+json",
      children: JSON.stringify(schema),
    })),
  });
}
